import { t } from "i18next";
import type { JSX } from "react";
import { useEffect } from "react";
import { type FieldErrors, FormProvider, useForm } from "react-hook-form";
import type { Template } from "@/common/types/templates";
import { HeadingEditor } from "@/components/forms/HeadingEditor";
import { LabeledSwitch } from "@/components/forms/Switch";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardFooter } from "@/components/ui/card";
import { WithDictation } from "@/components/ui/DictationButton";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "@/hooks/use-toast";
import type { TemplateFormSchemaType } from "@/schemas/template.schema";
import { SubscriptionOnlyButton } from "../SubscriptionOnlyButton/SubscriptionOnlyButton";
import {
  TemplateAssistantPanel,
  type TemplateAssistantPanelProps,
} from "./template-assistant-panel";

export type TemplateFormProps = {
  template?: Template;
  submitText: string;
  isSubmitting?: boolean;
  onSubmit: (data: TemplateFormSchemaType) => void;
  onCancel: () => void;
  // Shows the template assistant above the form when set.
  assistant?: TemplateAssistantPanelProps;
};

export const TemplateForm = (props: TemplateFormProps): JSX.Element => {
  const { template, submitText, onSubmit, onCancel, assistant } = props;

  const form = useForm<TemplateFormSchemaType>({
    defaultValues: {
      name: template?.name ?? "",
      headers: template?.headers ?? [],
      isDefault: !!template?.isDefault,
      optionalText: template?.optionalText ?? "",
    },
  });

  // Reset form when template prop changes
  useEffect(() => {
    if (template) {
      // Create a fresh copy of headers array to ensure form updates
      let headers: string[] = [];
      if (Array.isArray(template.headers)) {
        headers = [...template.headers];
      } else if (template.headers) {
        headers = [template.headers];
      }

      form.reset({
        name: template.name ?? "",
        headers: headers,
        isDefault: !!template.isDefault,
        optionalText: template.optionalText ?? "",
      });
    }
    // Only depend on template.public_id since we're using key prop for remounting
  }, [template?.public_id]);

  const handleError = (_: FieldErrors<TemplateFormSchemaType>): void => {
    toast({
      variant: "error",
      title: t("templates.createTemplateForm.toast.error.title"),
      description: t("templates.createTemplateForm.toast.error.description"),
    });
  };

  return (
    <FormProvider {...form}>
      {assistant && (
        <div className="mb-6">
          <TemplateAssistantPanel {...assistant} />
        </div>
      )}
      <form onSubmit={form.handleSubmit(onSubmit, handleError)}>
        <Card>
          <CardContent className="space-y-6 pt-6">
            {/* Header Field */}
            <div className="space-y-2">
              <Label htmlFor="header">
                {t("templates.createTemplateForm.templateNameLabel")}
              </Label>
              <WithDictation
                fieldLabel={t("dictation.field.fields.templateName")}
              >
                <Input
                  required
                  id="header"
                  placeholder={t(
                    "templates.createTemplateForm.headerPlaceholder",
                    "Enter template header",
                  )}
                  {...form.register("name")}
                />
              </WithDictation>
            </div>

            {/* Instructions Textbox */}
            <div className="space-y-2">
              <Label htmlFor="instructions">
                {t("templates.createTemplateForm.instructionsLabel")}
              </Label>
              <WithDictation
                fieldLabel={t("dictation.field.fields.templateInstructions")}
                multiline
              >
                <Textarea
                  className="min-h-[260px]"
                  id="instructions"
                  placeholder={t(
                    "templates.createTemplateForm.instructionsPlaceholder",
                  )}
                  {...form.register("optionalText")}
                />
              </WithDictation>
            </div>

            {/* Headers/Sections - Collapsible or at bottom */}
            <div className="space-y-2">
              <HeadingEditor name="headers" />
            </div>

            <LabeledSwitch
              className="space-x-2"
              name="isDefault"
              labelText={t(
                "templates.createTemplateForm.setDefaultCheckboxLabel",
              )}
            />
          </CardContent>

          <CardFooter className="flex justify-end *:ml-2">
            <SubscriptionOnlyButton
              disabled={props.isSubmitting || !form.formState.isValid}
              size="lg"
              type="submit"
            >
              {submitText}
            </SubscriptionOnlyButton>
            <Button size="lg" variant={"outline"} onClick={onCancel}>
              {t("templates.createTemplateForm.cancelButton")}
            </Button>
          </CardFooter>
        </Card>
      </form>
    </FormProvider>
  );
};
